import { observer } from 'mobx-react-lite';
import { calendarLoadingEvents } from './LoadingPanel.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { assertNever } from 'owa-assert';
import { CalendarLoadError } from 'owa-calendar-load-error';
import { setLoadFailureDialogState } from 'owa-calendar-load-error';
import { logUsage } from 'owa-analytics';
import { getSelectedCalendarsLoadStates } from 'owa-calendar-surface-calendars-load-state';

let styles = require('./LoadingPanel.scss');

export type LoadState = 'Loading' | 'Loaded' | 'Failed' | 'Throttled';

/**
 * This component renders UI for loading calendars loadingState
 * It shows loading spinner if none of calendars are yet Loaded
 * It shows an error dialog if ALL calendars failed to load
 * Note that calendar picker is also showing loading state on any individual calendar.
 */
export default observer(function LoadingPanel(props: { children?: React.ReactNode }) {
    return (
        <div className={styles.mainContent}>
            <CalendarLoadError />
            {getLoadStateView()}
            {props.children}
        </div>
    );
});

function getLoadState(): LoadState {
    let loadingStates = getSelectedCalendarsLoadStates();
    const allFailed =
        loadingStates.length > 0 && loadingStates.every(loadState => loadState === 'Failed');
    const someThrottledOthersFailed =
        loadingStates.length > 0 &&
        loadingStates.every(loadState => loadState === 'Failed' || loadState === 'Throttled');
    const someLoaded = loadingStates.some(loadState => loadState === 'Loaded');
    if (allFailed) {
        return 'Failed';
    } else if (someThrottledOthersFailed) {
        return 'Throttled';
    } else if (someLoaded) {
        return 'Loaded';
    } else {
        return 'Loading';
    }
}

function getLoadStateView() {
    const loadState = getLoadState();
    switch (loadState) {
        case 'Loading':
            return (
                <div className={styles.pageLoading}>
                    <Spinner size={SpinnerSize.large} label={loc(calendarLoadingEvents)} />
                </div>
            );
        case 'Loaded':
            setLoadFailureDialogState(true, 'None', true);
            return null;
        case 'Failed':
            logUsage('AllCalendarsFailedToLoad');
            // make the CalendarLoadError component visible
            setLoadFailureDialogState(false, 'Failed');
            return null;
        case 'Throttled':
            logUsage('AllCalendarsFailedorThrottledToLoad');
            // make the CalendarLoadError component visible
            setLoadFailureDialogState(false, 'Throttled');
            return null;
        default:
            return assertNever(loadState);
    }
}
